import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { CacheService } from 'src/app/services/cache.service';
import { FormSubmissionService } from 'src/app/services/form-submission.service';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { DIMBO_CONSTANT } from 'src/app/utils/constant';
import { ErrorDialogComponent } from '../error-dialog/error-dialog.component';
import { BrandcareFormResponse } from 'src/app/model/brandcare-form-response';
import { BrandcareFormService } from 'src/app/services/brandcare-form.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

/**
 * Handles BrandCare form lifecycle: loads backend data, builds reactive form, validates mileage, and submits.
 * Persists form state in cache, toggles Normal/Severe operation line data, and emits status messages.
 */
@Component({
  selector: 'app-allurecare-form',
  templateUrl: './allurecare-form.component.html',
  styleUrls: ['./allurecare-form.component.scss']
})
export class AllurecareFormComponent implements OnInit {
  private static mileageCheckDone = false;
  @Output() statusMessage = new EventEmitter<{ type: string, message: string }>();

  form: FormGroup;
  vehicleAgeDisplay: string = '';
  submitted: boolean = false; // Add this flag
  isFlowPanier: boolean = false;
  isMobile: boolean = false;
  isTablet: boolean = false;
  mileage: number = 0;
  maxMileage: number = 0;
  lastSubmittedMileage: number = 0;
  vinRRDIList: string[] = [];
  loading = true
  currentOperationLine: 'normal' | 'severe' = 'normal';
  showConditionSwitch: boolean = false;
  lastValidMileage: number = 0;

  previousMileage: number = null;

  brandcareFormData: BrandcareFormResponse | null = null;

  private isInitialMileagePrompt: boolean = true;

  constructor(private fb: FormBuilder, private formSubmissionService: FormSubmissionService,
    private cacheService: CacheService,
    private translate: TranslateService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private screenSizeService: ScreenSizeService,
    private cdr: ChangeDetectorRef,
    private brandcareFormService: BrandcareFormService
  ) { }

  ngOnInit(): void {
    this.screenSizeService.screenWidth$.subscribe((width) => {
      this.isMobile = width <= 480;
      this.isTablet = width > 480 && width <= 768;
    });

    this.loading = true;
    this.brandcareFormService.getBrandcareForm().subscribe({
      next: (response: BrandcareFormResponse) => {
        this.brandcareFormData = response;
        const contract = response.contractDetails;
        this.maxMileage = response.maxVehicleMileage;
        this.lastSubmittedMileage = response.lastSubmittedMileageValue;

        const opLine = response.operationLines[0];

        // Determine if both normal and severe exist
        const hasNormal = !!opLine?.normal;
        const hasSevere = !!opLine?.severe;
        this.showConditionSwitch = hasNormal && hasSevere;

        let defaultOpDetail = opLine.normal || opLine.severe; // pick whichever exists

        this.form = this.fb.group({
          conditionOfUse: [hasNormal ? 'Normal' : 'Severe'],
          reference: [defaultOpDetail?.reference || ''],
          typeOfService: [defaultOpDetail?.typeOfService || ''],
          maintenanceFrequency: [defaultOpDetail?.maintenanceFrequency || ''],
          panierFolderId: [response.panierFolderId || ''],
          dmsFolderId: [response.dmsFolderId || ''],
          vehicleMileage: [contract.vehicleMileage || '', Validators.required],
          mileageUnit: [contract.mileageUnit || 'Km'],
          vehicleAge: [contract.vehicleAge != null ? contract.vehicleAge : ''],
          customerEmail: [contract.customerEmail || '', [Validators.required, Validators.email, Validators.pattern(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/)]],
          loginId: [contract.loginId || ''],
          repairerId: [contract.vehicleBrandWorkshopId?.[0] || '', Validators.required],
          confirmTerms: [false, Validators.requiredTrue],
          agreeTerms: [false, Validators.requiredTrue]
        });


        if (hasNormal && !hasSevere) {
          this.form.patchValue({ conditionOfUse: 'Normal' });
          this.currentOperationLine = 'normal';
        } else if (!hasNormal && hasSevere) {
          this.form.patchValue({ conditionOfUse: 'Severe' });
          this.currentOperationLine = 'severe';
        }

        // Restore cached form data if available
        const cachedFormData = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_FORM_DATA);
        if (cachedFormData && this.form) {
          this.form.patchValue(cachedFormData);
        }

        // Persist form data on value changes
        this.form.valueChanges.subscribe(formValue => {
          this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, formValue);
        });

        // Optimized initial mileage logic for PNR and HUB
        const iss = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_ISS)?.toUpperCase();
        let mileageFromPayload = Number(contract.vehicleMileage);
        const hasMileageFromPayload = !isNaN(mileageFromPayload) && mileageFromPayload > 0;
        const hasLastSubmittedMileage = !!this.lastSubmittedMileage && this.lastSubmittedMileage > 0;
        let initialPromptDone = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_INITIAL_MILEAGE_PROMPT) === 'true';

        // Remove old confirmation logic and replace with:
        if ((iss === 'PNR' || iss === 'HUB') && !initialPromptDone) {
          if (hasMileageFromPayload) {
            // Always set payload mileage, no popup
            this.form.patchValue({ vehicleMileage: mileageFromPayload });
            this.previousMileage = mileageFromPayload;
            this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
          } else if (hasLastSubmittedMileage) {
            // No payload, but lastSubmitted exists
            this.form.patchValue({ vehicleMileage: '' });
            this.previousMileage = null;
            this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
          } else {
            // No payload, no lastSubmitted
            this.form.patchValue({ vehicleMileage: '' });
            this.previousMileage = null;
            this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
          }
          this.cacheService.setItem(DIMBO_CONSTANT.CACHE_INITIAL_MILEAGE_PROMPT, 'true');
        } else {
          // If initialPromptDone, restore previousMileage from form value
          this.previousMileage = Number(this.form.get('vehicleMileage')?.value) || null;
        }

        // Subscribe to valueChanges first!
        this.form.get('vehicleMileage')?.valueChanges
          .pipe(debounceTime(2000))
          .subscribe((newValue) => {
            if (newValue === '' || newValue === null || newValue === undefined) {
              this.previousMileage = null;
              return;
            }
            newValue = Number(newValue);

            // Check for invalid mileage
            if (newValue <= 0) {
              this.form.get('vehicleMileage')?.setErrors({ invalidMileage: true });
              const dialogRef = this.dialog.open(ErrorDialogComponent, {
                disableClose: true,
                data: {
                  message: this.translate.instant('dimbo.i18n.brandcareForm.error.vehicleMileagePositive'),
                  type: 'error'
                }
              });
              dialogRef.afterClosed().subscribe(() => {
                this.form.patchValue({ vehicleMileage: this.previousMileage }, { emitEvent: false });
                this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
              });
              return;
            }

            // Check for max mileage
            if (newValue > this.maxMileage) {
              this.form.get('vehicleMileage')?.setErrors({ maxMileage: true });
              const dialogRef = this.dialog.open(ErrorDialogComponent, {
                disableClose: true,
                data: {
                  message: this.translate.instant('dimbo.i18n.brandcareForm.error.mileage.max', {
                    brandCare: this.getBrandCareName(),
                    maxMileage: this.maxMileage,
                    mileageUnit: this.form.get('mileageUnit')?.value
                  }),
                  type: 'error'
                }
              });
              dialogRef.afterClosed().subscribe(() => {
                this.form.patchValue({ vehicleMileage: this.previousMileage }, { emitEvent: false });
                this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
              });
              return;
            }

            // Confirmation logic for lastSubmittedMileage
            if (hasLastSubmittedMileage && newValue < this.lastSubmittedMileage) {
              const dialogRef = this.dialog.open(ErrorDialogComponent, {
                width: '400px',
                disableClose: true,
                data: {
                  message: this.translate.instant('dimbo.i18n.brandcareForm.error.mileage.lessThanLastSubmitted'),
                  type: 'confirm'
                }
              });
              dialogRef.afterClosed().subscribe(result => {
                if (result === true) {
                  this.previousMileage = newValue;
                  this.form.patchValue({ vehicleMileage: newValue }, { emitEvent: false });
                  this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
                } else {
                  if (this.isInitialMileagePrompt) {
                    // On initial load, revert to lastSubmittedMileage
                    this.previousMileage = this.lastSubmittedMileage;
                    this.form.patchValue({ vehicleMileage: this.lastSubmittedMileage }, { emitEvent: false });
                    this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
                    this.isInitialMileagePrompt = false; // Only for first time
                  } else {
                    // On subsequent changes, revert to previous valid value
                    this.form.patchValue({ vehicleMileage: this.previousMileage }, { emitEvent: false });
                    this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
                  }
                }
              });
              return;
            }

            // Confirmation logic for previousMileage
            if (this.previousMileage !== null && newValue < this.previousMileage) {
              const dialogRef = this.dialog.open(ErrorDialogComponent, {
                width: '400px',
                disableClose: true,
                data: {
                  message: this.translate.instant('dimbo.i18n.brandcareForm.error.mileage.lessThanPrevious'),
                  type: 'confirm'
                }
              });
              dialogRef.afterClosed().subscribe(result => {
                if (result === true) {
                  this.previousMileage = newValue;
                  this.form.patchValue({ vehicleMileage: newValue }, { emitEvent: false });
                  this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
                } else {
                  this.form.patchValue({ vehicleMileage: this.previousMileage }, { emitEvent: false });
                  this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
                }
              });
              return;
            }

            // If valid and not less than previous/lastSubmitted, update previousMileage
            this.previousMileage = newValue;
            this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);

            // Clear custom errors if valid
            const errors = this.form.get('vehicleMileage')?.errors;
            if (errors) {
              delete errors.invalidMileage;
              delete errors.maxMileage;
              if (Object.keys(errors).length === 0) {
                this.form.get('vehicleMileage')?.setErrors(null);
              } else {
                this.form.get('vehicleMileage')?.setErrors(errors);
              }
            }
            this.isInitialMileagePrompt = false; // Ensure flag is reset after first change
          });

        // Now patch the initial value (this will trigger the above logic)
        if ((iss === 'PNR' || iss === 'HUB') && !initialPromptDone) {
          if (hasMileageFromPayload) {
            this.form.patchValue({ vehicleMileage: mileageFromPayload });
            this.previousMileage = mileageFromPayload;
            this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
            this.isInitialMileagePrompt = true; // Set flag for initial load
          } else if (hasLastSubmittedMileage) {
            this.form.patchValue({ vehicleMileage: '' });
            this.previousMileage = null;
            this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
            this.isInitialMileagePrompt = false;
          } else {
            this.form.patchValue({ vehicleMileage: '' });
            this.previousMileage = null;
            this.cacheService.setItem(DIMBO_CONSTANT.CACHE_FORM_DATA, this.form.value);
            this.isInitialMileagePrompt = false;
          }
          this.cacheService.setItem(DIMBO_CONSTANT.CACHE_INITIAL_MILEAGE_PROMPT, 'true');
        } else {
          this.previousMileage = Number(this.form.get('vehicleMileage')?.value) || null;
          this.isInitialMileagePrompt = false;
        }

        this.vinRRDIList = contract.vehicleBrandWorkshopId || [];
        this.loading = false;
        this.cdr.detectChanges();


        this.mileage = this.form.get('vehicleMileage')?.value;
        this.lastValidMileage = this.mileage; // <-- Initialize lastValidMileage
        
        this.isFlowPanier = this.checkIsFlowPanier();
        this.setRepairerIdBasedOnFlow();
      },
      error: (err) => {
        this.loading = false;
        if ([400, 403, 404, 500, 502].includes(err.status) && err.error && err.error.errorDetails) {
          let message: string;
          // Special handling for vehicle age exceeded
          if (
            err.error.name === 'VEHICLE_AGE_EXCEEDED' &&
            err.error.errorDetails.issue === 'dimbo.i18n.api.error.vehicleAge.exceeded'
          ) {
            message = this.translate.instant('dimbo.i18n.api.error.vehicleAge.exceeded', {
              brandCare: this.getBrandCareName(),
              vehicleAge: err.error.errorDetails.fieldValue
            });
          } else {
            // Default: just translate the key
            message = this.translate.instant(err.error.errorDetails.issue);
          }
          this.statusMessage.emit({ type: 'error', message });
        } else {
          // fallback
          this.statusMessage.emit({ type: 'error', message: this.translate.instant('dimbo.i18n.brandcareForm.error.generic') });
        }
      }
    });

  }

  ngAfterViewInit(): void {
    // Optionally, ensure lastValidMileage is set after view init if needed
    this.lastValidMileage = this.form?.get('vehicleMileage')?.value;
  }



  /**
   * Sets the value of the 'repairerId' form control based on the current flow.
   * 
   * - If the flow is 'Panier' (`isFlowPanier` is true), sets the 'repairerId' to a predefined value ('XFB1010').
   * - Otherwise, initializes the `vinRRDIList` with a default list of repairer IDs and clears the 'repairerId' form control without emitting an event.
   *
   * This method is responsible for ensuring the correct repairer ID is set or cleared
   * depending on the application's flow context.
   */
  private setRepairerIdBasedOnFlow() {
    if (this.isFlowPanier) {

      const receptionRRDI = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_RECEPTION_RRDI) || '';


      this.form.get('repairerId')?.setValue(receptionRRDI);
    } else {

      const vinRRDI = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN_RRDI) || ['XFB1010', 'DCB2310'];
      this.vinRRDIList = vinRRDI;

      if (this.vinRRDIList.length === 1) {
        this.form.get('repairerId')?.setValue(this.vinRRDIList[0], { emitEvent: false });
        this.form.get('repairerId')?.enable({ emitEvent: false });
      }
      else {
        this.form.get('repairerId')?.setValue('', { emitEvent: false });
        this.form.get('repairerId')?.enable({ emitEvent: false });
      }
    }
  }

  // Set value for panier flow true or false
  checkIsFlowPanier(): boolean {
    const iss = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_ISS).toUpperCase();
    return iss === 'PNR';
  }




  /**
   * Determines whether a specific validation error exists on a form control.
   * Returns true if the control has the specified error and has been touched,
   * or if the form has been submitted.
   *
   * @param controlName - The name of the form control to check.
   * @param errorName - The name of the validation error to look for.
   * @returns True if the error exists and should be displayed; otherwise, false.
   */
  hasError(controlName: string, errorName: string): boolean {
    const control = this.form.get(controlName);
    // Show error if control is touched or form has been submitted
    return !!control && (control.touched || this.submitted) && control.hasError(errorName);
  }

  /**
   * Determines if a checkbox form control has an error based on submission state.
   * Returns `true` if the form has been submitted and the specified checkbox control is unchecked.
   *
   * @param controlName - The name of the checkbox form control to check.
   * @returns `true` if the checkbox is in an error state, otherwise `false`.
   */
  isCheckboxError(controlName: string): boolean {
    return this.submitted && this.form.get(controlName)?.value === false;
  }



  /**
   * Toggles the 'conditionOfUse' form control value between 'Normal' and 'Arduous',
   * updates the form with the new value, and triggers maintenance frequency update.
   */
  onConditionOfUseToggle(): void {
    const currentCondition = this.form.get('conditionOfUse')?.value;
    const newCondition = currentCondition === 'Normal' ? 'Severe' : 'Normal';
    this.form.patchValue({ conditionOfUse: newCondition });

    // Update operation line and maintenance frequency from API response
    if (this.brandcareFormData) {
      const opLineData = this.brandcareFormData.operationLines[0];
      if (newCondition === 'Normal') {
        this.form.patchValue({
          reference: opLineData.normal.reference,
          typeOfService: opLineData.normal.typeOfService,
          maintenanceFrequency: opLineData.normal.maintenanceFrequency
        });
        this.currentOperationLine = 'normal';
      } else {
        this.form.patchValue({
          reference: opLineData.severe.reference,
          typeOfService: opLineData.severe.typeOfService,
          maintenanceFrequency: opLineData.severe.maintenanceFrequency
        });
        this.currentOperationLine = 'severe';
      }
    }
  }


  /**
   * Handles the form submission logic for the AllureCare form.
   * 
   * - Marks the form as submitted and validates all fields.
   * - Collects and displays error messages for invalid fields, including:
   *   - Customer email format
   *   - Repairer's confirmation checkbox
   *   - GDPR agreement checkbox
   *   - Maximum vehicle mileage
   * - If the new mileage is less than the previous value, prompts the user for confirmation.
   * - If the form is valid and all checks pass, submits the form data via the formSubmissionService.
   * - Displays appropriate error dialogs for validation failures.
   */
  onSubmit(): void {
    this.submitted = true; // Set submitted to true
    this.form.markAllAsTouched();
    const errors: { field: string; message: string }[] = [];


    // Highlight all invalid fields
    // 1. Customer email validation (regex)
    const emailControl = this.form.get('customerEmail');

    if (!emailControl || emailControl.invalid) {
      errors.push({
        field: 'customerEmail',
        message: this.translate.instant('dimbo.i18n.brandcareForm.error.email')
          || 'Please check the customer email before re-submitting the form'
      });
    }

    // 2. Repairer's confirmation checkbox
    const confirmTermsControl = this.form.get('confirmTerms');
    if (!confirmTermsControl?.value) {
      errors.push({
        field: 'confirmTerms',
        message: this.translate.instant('dimbo.i18n.brandcareForm.error.confirmTerms.checkbox')
          || 'Please confirm the job has been done'
      });
    }

    // 3. GDPR confirmation checkbox
    const agreeTermsControl = this.form.get('agreeTerms');
    if (!agreeTermsControl?.value) {
      errors.push({
        field: 'agreeTerms',
        message: this.translate.instant('dimbo.i18n.brandcareForm.error.agreeTerms.checkbox')
          || 'Please agree to GDPR terms'
      });
    }


    // 🚨 Add mileage > 0 validation
    const currentMileage = this.form.get('vehicleMileage')?.value;
    if (currentMileage <= 0) {
      errors.push({
        field: 'vehicleMileage',
        message: this.translate.instant('dimbo.i18n.brandcareForm.error.vehicleMileagePositive')
      });
    }


    // 🚨 Add max mileage validation here
    if (currentMileage > this.maxMileage) {
      errors.push({
        field: 'vehicleMileage',
        message: this.translate.instant('dimbo.i18n.brandcareForm.error.mileage.max', {
          brandCare: this.getBrandCareName(),
          maxMileage: this.maxMileage,
          mileageUnit: this.form.get('mileageUnit')?.value
        })
      });
    }



    if (errors.length > 0) {
      // Show toaster message
      if (errors.length === 1) {
        this.dialog.open(ErrorDialogComponent, {
          disableClose: true,
          data: {

            message: errors[0].message, // Customize as needed
            type: 'error'
          }
        });
        // this.toastr.error(errors[0].message);
      } else {
        this.dialog.open(ErrorDialogComponent, {
          disableClose: true,
          data: {
            message: this.translate.instant('dimbo.i18n.brandcareForm.error.submission') || 'Some errors were found. Please correct them before resubmitting.',
            type: 'error'
          }
        });
        // this.toastr.error(
        //   this.translate.instant('dimbo.i18n.brandcareForm.error.generic')
        //   || 'Some errors were found. Please correct them before resubmitting.'
        // );
      }
      return;
    }

    // 🚨 Add Mileage Validation Here
    const previousMileage = this.mileage;

    if (currentMileage < previousMileage) {
      const dialogRef = this.dialog.open(ErrorDialogComponent, {
        width: '400px',
        disableClose: true,
        data: {
          message: this.translate.instant('dimbo.i18n.brandcareForm.error.mileage.lessThanPrevious'),
          type: 'confirm'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === true) {
          this.mileage = currentMileage;
          this.form.patchValue({ vehicleMileage: currentMileage });
        } else {
          this.form.patchValue({ vehicleMileage: previousMileage });
        }
      });
    }
    else {

      if (this.form.valid) {
        this.loading = true;
     
        const formValue = this.form.value;
        const iss = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_ISS);
        const locale = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_LOCALE);
        const contributionFlag = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_CONTRIBUTION_FLAG);
        const userRrdi = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_RRDI);
        const vin = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN);
        const vinBrand = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN_BRAND);
        const vehicleType = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VEHICLE_TYPE);
        const engineType = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_ENGINE_TYPE);
        const model = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_MODEL);
        const warrantyStartDate = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_WARRANTY_START_DATE);
        // CAP-39922 DMBR-64
        const physicalSite = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_PHSYSICAL_SITE);

        let selectedOpDetail = null;
        if (this.brandcareFormData) {
          const opLine = this.brandcareFormData.operationLines[0];
          selectedOpDetail = formValue.conditionOfUse === 'Normal' ? opLine.normal : opLine.severe;
        }


        // Prepare the payload with updated and new keys
        const payload: any = {
          iss,
          locale,
          userRrdi,
          vin,
          vinBrand,
          vehicleType,
          engineType,
          model,
          warrantyStartDate,
          // CAP-39922 DMBR-64
          physicalSite,
          pnrFolderId: formValue.panierFolderId,
          dmsFolderId: formValue.dmsFolderId,
          mileageUnit: formValue.mileageUnit,
          vehicleAge: formValue.vehicleAge,
          email: formValue.customerEmail,
          // Key name changes
          userId: formValue.loginId,
          vinRrdi: formValue.repairerId,
          mileage: formValue.vehicleMileage,
          workLineOperationCode: formValue.reference,
          workLineLabel: formValue.typeOfService,
          maintenanceFrequency: formValue.maintenanceFrequency,
          conditionOfUse: formValue.conditionOfUse,
          confirmTerms: formValue.confirmTerms,
          agreeTerms: formValue.agreeTerms,
          contributionFlag,
          // Add yearCycle & distanceCycle from selected operation line
          yearCycle: selectedOpDetail?.yearCycle?.cycle ? selectedOpDetail.yearCycle.cycle * 12 : null,
          distanceCycle: selectedOpDetail?.distanceCycle?.cycle,
          // Add toleranceMonths & toleranceMileage from BrandcareFormResponse
          toleranceMonths: this.brandcareFormData?.toleranceMonths,
          toleranceMileage: this.brandcareFormData?.toleranceMileage
        };

        // Add workLineId only if iss is PNR
        if (iss && iss.toUpperCase() === 'PNR' && this.brandcareFormData?.matchedWorkLineId) {
          payload.workLineId = this.brandcareFormData.matchedWorkLineId;
        }

        // Remove panierFolderId & dmsFolderId if iss is HUB
        if (iss && iss.toUpperCase() === 'HUB') {
          delete payload.pnrFolderId;
          delete payload.dmsFolderId;
        }

     
        // Submit the transformed payload
        this.formSubmissionService.submitForm(payload).subscribe(
          (response) => {
            this.loading = false;
            this.handleSubmitSuccess(response);
          },
          (error) => {
            this.loading = false;
            // Start: CAP-39922 DMBR-67
            if (error.status === 409 && error.error?.name === 'GRACE_PERIOD_VIOLATION') {
              
              // Extract gracePeriodHours from error response fieldValue
              const gracePeriodHours = error.error?.errorDetails?.fieldValue || '';
              
              // Show grace period confirmation popup with dynamic message
              const dialogRef = this.dialog.open(ErrorDialogComponent, {
                disableClose: true,
                data: {
                  message: this.translate.instant('dimbo.i18n.brandcareForm.warning.gracePeriod', {
                    gracePeriodHours: gracePeriodHours
                  }),
                  type: 'confirm'
                }
              });

              dialogRef.afterClosed().subscribe(result => {
                if (result === true) {
                  // User confirmed: resubmit with bypassGracePeriod = true
                  this.loading = true;
                  // Avoid spread operator to fix tslib issue
                  const bypassPayload = Object.assign({}, payload, { bypassGracePeriod: true });
                  this.formSubmissionService.submitForm(bypassPayload).subscribe(
                    (res) => {
                      this.loading = false;
                      this.handleSubmitSuccess(res);
                    },
                    (err) => {
                      this.loading = false;
                      this.handleSubmitError(err);
                    }
                  );
                }
                // End: CAP-39922 DMBR-67
                // If cancelled: user stays on form page — no action needed
              });
            } else {
              this.handleSubmitError(error);
            }
          }
        );
      }
      else {
        this.form.markAllAsTouched();
      }
    }
  }

  /**
   * Handles successful form submission response.
   * Emits appropriate status message based on response status code.
   *
   * @param response - The API response object
   */
  private handleSubmitSuccess(response: any): void {
    const contractId = response.cemContractId || '';

    if (response.statusCode === 200) {
      this.statusMessage.emit({
        type: 'success',
        message: `${this.translate.instant(response.message)} ${contractId} ${this.translate.instant('dimbo.i18n.api.formSubmission.success.suffix')}`
      });
      this.cacheService.removeItem(DIMBO_CONSTANT.CACHE_FORM_DATA);
    } else if (response.statusCode === 206) {
      this.statusMessage.emit({
        type: 'warning',
        message: `${this.translate.instant(response.message)} ${contractId} ${this.translate.instant('dimbo.i18n.api.error.logotel.failed.suffix')}`
      });
      this.cacheService.removeItem(DIMBO_CONSTANT.CACHE_FORM_DATA);
    } else {
      this.statusMessage.emit({
        type: 'error',
        message: this.translate.instant('dimbo.i18n.brandcareForm.error.generic') || 'Something went wrong. Please try again later.'
      });
    }
  }

  /**
   * Handles form submission error response.
   * Displays appropriate error dialog or status message based on error status code.
   *
   * @param error - The HTTP error object
   */
  private handleSubmitError(error: any): void {
    if (error?.error?.statusCode === 503) {
      this.dialog.open(ErrorDialogComponent, {
        disableClose: true,
        data: {
          message: this.translate.instant(error?.error?.message) || 'Service unavailable. Please resubmit the form.',
          type: 'error'
        }
      });
    } else if (error?.error?.statusCode === 403 || error?.error?.statusCode === 400 || error?.status === 401) {
      this.statusMessage.emit({
        type: 'error',
        message: this.translate.instant(error?.error?.message) || this.translate.instant('dimbo.i18n.brandcareForm.error.generic')
      });
    } else {
      this.statusMessage.emit({
        type: 'error',
        message: this.translate.instant('dimbo.i18n.brandcareForm.error.generic')
      });
    }
  }

  closeTab() {
    window.close();
    setTimeout(() => {
      if (!window.closed) {
        alert(this.translate.instant('dimbo.i18n.brandcareForm.error.tab.close'));
      }
    }, 200);
  }


  // Utility function to get brandCare name
  private getBrandCareName(): string {
    const vinBrand = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_VIN_BRAND);
    const locale = this.cacheService.getItem(DIMBO_CONSTANT.CACHE_JWT_LOCALE);
    if (vinBrand === 'AP') {
      return 'Peugeot Care';
    } else if (vinBrand === 'AC') {
      return 'Citroen Care';
    } else if (vinBrand === 'DS') {
      return 'DS Care';
    } else if (vinBrand === 'OV') {
      return (locale === 'en_GB') ? 'Vauxhall Care' : 'Opel Care';
    }
    return 'Brand Care';
  }

}